import { VOICE_MAX_SECONDS, VOICE_MIN_BYTES, VOICE_MIN_SECONDS, VOICE_WAVE_BARS } from '../constants/voice-note.constants';
import { VoiceRecording } from '../interfaces/voice-note.interface';

const MIME_CANDIDATES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'];

export type VoiceRecorderError = 'unsupported' | 'mic-denied' | 'too-short' | 'discarded';

export class VoiceRecorder {
  private recorder?: MediaRecorder;
  private stream?: MediaStream;
  private chunks: Blob[] = [];
  private discard = false;
  private micRequest = false;
  private timer?: ReturnType<typeof setInterval>;
  private meterFrame?: number;
  private meterContext?: AudioContext;
  private levelHistory: number[] = [];
  private seconds = 0;
  private alive = true;

  destroy(): void {
    this.alive = false;
    this.finish(true);
  }

  async start(onTick: (seconds: number, bars: number[]) => void): Promise<VoiceRecording | VoiceRecorderError> {
    if (this.recorder || this.micRequest || typeof MediaRecorder === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      return 'unsupported';
    }

    this.micRequest = true;
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      return 'mic-denied';
    } finally {
      this.micRequest = false;
    }

    if (!this.alive) {
      stream.getTracks().forEach((track) => track.stop());
      return 'unsupported';
    }

    const mimeType = MIME_CANDIDATES.find((type) => MediaRecorder.isTypeSupported(type)) ?? '';
    const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
    this.chunks = [];
    this.discard = false;
    this.levelHistory = [];
    this.seconds = 0;

    const done = new Promise<VoiceRecording | VoiceRecorderError>((resolve) => {
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) this.chunks.push(event.data);
      };
      recorder.onstop = () => {
        const seconds = this.seconds;
        const discard = this.discard;
        const levels = this.levelHistory.slice();
        const blob = new Blob(this.chunks, { type: recorder.mimeType || mimeType || 'audio/webm' });
        this.chunks = [];
        this.levelHistory = [];
        this.releaseMic();
        if (discard || !this.alive) {
          resolve('discarded');
          return;
        }
        if (seconds < VOICE_MIN_SECONDS || blob.size < VOICE_MIN_BYTES) {
          resolve('too-short');
          return;
        }
        void this.readWaveform(blob, levels, seconds).then(resolve);
      };
    });

    this.stream = stream;
    this.recorder = recorder;
    recorder.start();
    this.startMeter(stream, onTick);
    onTick(0, flatWave());
    this.timer = setInterval(() => {
      this.seconds += 1;
      if (this.seconds >= VOICE_MAX_SECONDS) this.finish(false);
    }, 1000);

    return done;
  }

  finish(discard: boolean): void {
    this.discard = discard;
    const recorder = this.recorder;
    if (recorder && recorder.state !== 'inactive') {
      recorder.stop();
      return;
    }
    this.releaseMic();
  }

  private async readWaveform(blob: Blob, levels: number[], seconds: number): Promise<VoiceRecording> {
    try {
      const context = new AudioContext();
      const audioBuffer = await context.decodeAudioData(await blob.arrayBuffer());
      await context.close();
      return {
        blob,
        seconds: Math.max(1, Math.round(audioBuffer.duration)),
        waveform: peaks(audioBuffer.getChannelData(0)),
      };
    } catch {
      return {
        blob,
        seconds: Math.max(1, seconds),
        waveform: barsFromLevels(levels),
      };
    }
  }

  private startMeter(stream: MediaStream, onTick: (seconds: number, bars: number[]) => void): void {
    const context = new AudioContext();
    const source = context.createMediaStreamSource(stream);
    const analyser = context.createAnalyser();
    analyser.fftSize = 128;
    analyser.smoothingTimeConstant = 0.62;
    source.connect(analyser);
    this.meterContext = context;
    void context.resume();
    const bins = new Uint8Array(analyser.frequencyBinCount);
    let previous = flatWave();
    const tick = () => {
      analyser.getByteFrequencyData(bins);
      const step = bins.length / VOICE_WAVE_BARS;
      const bars: number[] = [];
      let energy = 0;
      for (let i = 0; i < VOICE_WAVE_BARS; i++) {
        const start = Math.floor(i * step);
        const end = Math.max(start + 1, Math.floor((i + 1) * step));
        let peak = 0;
        for (let j = start; j < end; j++) peak = Math.max(peak, bins[j] ?? 0);
        const level = Math.max(0.12, Math.min(1, peak / 220));
        bars.push(level);
        energy += level;
      }
      this.levelHistory.push(energy / VOICE_WAVE_BARS);
      previous = bars.map((level, index) => (previous[index] ?? level) * 0.4 + level * 0.6);
      onTick(this.seconds, previous);
      this.meterFrame = requestAnimationFrame(tick);
    };
    this.meterFrame = requestAnimationFrame(tick);
  }

  private releaseMic(): void {
    if (this.timer) clearInterval(this.timer);
    this.timer = undefined;
    if (this.meterFrame) cancelAnimationFrame(this.meterFrame);
    this.meterFrame = undefined;
    const context = this.meterContext;
    this.meterContext = undefined;
    if (context && context.state !== 'closed') void context.close();
    this.stream?.getTracks().forEach((track) => track.stop());
    this.stream = undefined;
    this.recorder = undefined;
  }
}

export function flatWave(): number[] {
  return Array.from({ length: VOICE_WAVE_BARS }, () => 0.2);
}

export function voiceBarHeight(level: number): number {
  return 4 + Math.max(0, Math.min(1, level)) * 22;
}

function peaks(channel: Float32Array): number[] {
  const out: number[] = [];
  const size = Math.max(1, Math.floor(channel.length / VOICE_WAVE_BARS));
  for (let i = 0; i < VOICE_WAVE_BARS; i++) {
    const start = i * size;
    const end = Math.min(channel.length, start + size);
    let peak = 0;
    const step = Math.max(1, Math.floor((end - start) / 40));
    for (let j = start; j < end; j += step) {
      const value = Math.abs(channel[j] ?? 0);
      if (value > peak) peak = value;
    }
    out.push(peak);
  }
  return normalizeBars(out);
}

function barsFromLevels(levels: number[]): number[] {
  if (!levels.length) return flatWave();
  const out: number[] = [];
  const size = levels.length / VOICE_WAVE_BARS;
  for (let i = 0; i < VOICE_WAVE_BARS; i++) {
    const start = Math.floor(i * size);
    const end = Math.max(start + 1, Math.floor((i + 1) * size));
    let peak = 0;
    for (let j = start; j < end; j++) peak = Math.max(peak, levels[j] ?? 0);
    out.push(peak);
  }
  return normalizeBars(out);
}

function normalizeBars(values: number[]): number[] {
  const max = Math.max(...values, 0.001);
  return values.map((value) => Math.max(0.16, Math.min(1, value / max)));
}
